
import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../../auth/context";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ErrorAlert, Alert } from "../../utils";
import { EmailInput, TextField } from "../../utils/form";
import { OTP_PENDING_STATUSES, PASSWORD_RESET_REQUIRED_STATUS } from "../../apis/loginGatewayApi";
import { Link } from "react-router-dom";
import _ from "lodash";
import Logo from "../../utils/Logo";

const TermsAndConditionsLabel = () => {
  return (
    <Typography align="center" fontSize="13px">
      Al iniciar sesión, aceptás nuestros <Typography color="primary" component={Link} to="/termsAndConditions" target="_blank" fontSize=".8rem">términos y condiciones</Typography>.
    </Typography>
  );
};

const invalidCredentialsMessage = "Credenciales inválidas";
const inactiveAccountMessage = "Tu cuenta aún no está activa. Si ya pasaron más de 24hs hábiles desde que te registraste, contactanos a consultas@recetario.com.ar";
const blockedLicenseMessage = "Tu matrícula está bloqueada. Contactanos a consultas@recetario.com.ar para más información.";
const invalidCodeMessage = "Código incorrecto.";
const expiredChallengeMessage = "El código expiró o se agotaron los intentos. Iniciá sesión nuevamente.";
const activeCodeMessage = "Ya tenés un código de verificación activo. Revisá tu mail (y la carpeta de spam) e ingresalo a continuación.";
const passwordResetRequiredMessage = "Tu contraseña venció y tenés que cambiarla por seguridad. Usá la opción \"Olvidé mi contraseña\" de abajo para definir una nueva, y volvé a iniciar sesión.";

// Sin error no hay nada que mostrar -- si esto devolviera un mensaje igual (como pasaba
// antes con el último caso del switch), el alert lo mostraría destellando mientras se
// cierra solo (auto-hide del Snackbar), aunque el error real ya no exista.
function getLoginErrorMessage(error, attemptsRemaining) {
  if (_.isEmpty(error)) return "";

  // _.includes (a diferencia del método nativo de string) tolera que el mensaje sea
  // null/undefined sin explotar -- pasa si viene ausente o explícitamente null.
  const isInactiveAccount = _.includes(_.get(error, "response.data.message"), "active");
  if (isInactiveAccount) return inactiveAccountMessage;

  const isBlockedLicense = _.includes(_.get(error, "response.data.message"), "blocked");
  if (isBlockedLicense) return blockedLicenseMessage;

  const status = _.get(error, "response.data.status");
  if (status === "expired_or_locked") return expiredChallengeMessage;

  if (status === "invalid_code") {
    const remainingSuffix = _.isNil(attemptsRemaining) ? "" : ` Intentos restantes: ${attemptsRemaining}.`;
    return `${invalidCodeMessage}${remainingSuffix}`;
  }

  return invalidCredentialsMessage;
}

const Login = () => {
  const form = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  });
  const codeForm = useForm({
    defaultValues: {
      code: ''
    }
  });
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [challengeId, setChallengeId] = useState(null);
  const [loginStatus, setLoginStatus] = useState(null);
  const [error, setError] = useState();
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [notice, setNotice] = useState(null);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const auth = useAuth();
  // Guarda contra respuestas que llegan fuera de orden (doble submit, red lenta): solo el
  // resultado del pedido más reciente puede aplicar sus cambios de estado.
  const latestLoginRequestId = useRef(0);
  const lastErrorMessageRef = useRef("");

  const { handleSubmit, setValue, control } = form;
  const { handleSubmit: handleCodeSubmit, setValue: setCodeValue, control: codeControl } = codeForm;

  // `clearError`: el camino automático desde verify() (código expirado/bloqueado) necesita
  // que el mensaje de error sobreviva la transición para poder mostrarlo en la pantalla de
  // credenciales -- el click manual en "Volver a iniciar sesión" sí tiene que limpiarlo,
  // si no queda pegado un error viejo (ej. "Código incorrecto") en una pantalla en blanco.
  const backToCredentials = (clearError = false) => {
    setStep("credentials");
    setChallengeId(null);
    setAttemptsRemaining(null);
    setNotice(null);
    setValue("password", "");
    setCodeValue("code", "");
    if (clearError) {
      setError(null);
      // Sin esto, el mensaje viejo (ej. "Código incorrecto") queda destellando sobre la
      // pantalla de credenciales recién reseteada mientras el alert termina de cerrarse.
      lastErrorMessageRef.current = "";
    }
  };

  const login = ({ email, password }) => {
    const requestId = ++latestLoginRequestId.current;
    setLoginStatus("loading");
    setError(null);
    setNotice(null);
    setPasswordResetRequired(false);
    auth.signin(email, password)
    .then(result => {
      if (requestId !== latestLoginRequestId.current) return;
      setLoginStatus(null);
      if (result?.status === PASSWORD_RESET_REQUIRED_STATUS) {
        setPasswordResetRequired(true);
        setValue("password", "");
        return;
      }
      const isAlreadyActive = result?.status === "challenge_already_active";
      if (OTP_PENDING_STATUSES.includes(result?.status)) {
        setChallengeId(result.challengeId);
        setAttemptsRemaining(null);
        setStep("otp");
        setNotice(isAlreadyActive ? activeCodeMessage : null);
      }
    })
    .catch(error => {
      if (requestId !== latestLoginRequestId.current) return;
      setError(error);
      setLoginStatus(null);
      setValue("password", "");
    });
  };

  const verify = ({ code }) => {
    setLoginStatus("loading");
    setError(null);
    auth.verifyCode(challengeId, code)
    .catch(error => {
      const status = _.get(error, "response.data.status");
      if (status === "expired_or_locked") {
        backToCredentials();
      } else {
        setNotice(null);
        setAttemptsRemaining(_.get(error, "response.data.attemptsRemaining"));
        setCodeValue("code", "");
      }
      setError(error);
      setLoginStatus(null);
    });
  };

  const onAlertClose = () => setError(null);

  const isLoading = loginStatus === "loading";
  const email = form.getValues("email");
  const failed = !_.isEmpty(error);
  const currentMessage = getLoginErrorMessage(error, attemptsRemaining);

  // El Snackbar sigue montado (desvaneciéndose) un instante después de que `open` pasa a
  // false, y en ese momento ya no hay error -- si `message` se vaciara ahí, se vería un
  // alert vacío cerrándose en vez de terminar de mostrar el último texto real. Por eso
  // guardamos el último mensaje no vacío (en un efecto, no durante el render) y lo usamos
  // de respaldo mientras se cierra.
  useEffect(() => {
    if (currentMessage) lastErrorMessageRef.current = currentMessage;
  }, [currentMessage]);

  const message = currentMessage || lastErrorMessageRef.current;

  return (
    <Stack display="flex" flexDirection="column" spacing="2rem" boxSizing="border-box" width="100%" height="100%">
      {step === "credentials" ? (
        <form onSubmit={handleSubmit(login)} style={{ height: "100%", width: "100%" }} noValidate>
          <Paper style={{ height: "100%", display: "flex", alignItems: "center", backgroundPosition: "top", backgroundRepeat: "no-repeat", backgroundImage: "url(https://res.cloudinary.com/dw86qy8xu/image/upload/v1694744779/loginSplash_syhrc1.jpg)", backgroundSize: "cover" }}>
            <Grid item xs={12} sm={8} md={6} lg={4} marginX="auto" boxSizing="border-box" p="1rem">
              <Box p="1rem" boxSizing="border-box" borderRadius="16px" boxShadow="0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px rgba(0, 0, 0, 0.15)" bgcolor="#FAFAFA">
                <Stack direction="column" alignItems="center" p="1rem" spacing="2rem">
                  <Logo />
                  <Stack width="100%" spacing="1rem" pt="1.5rem">
                    <EmailInput form={form} style={{ width: "100%" }} required autoComplete="username" type="email" />
                    <TextField control={control} name="password" type="password" label="Contraseña" variant="outlined" style={{ width: "100%" }} required autoComplete="current-password"/>
                  </Stack>
                  <LoadingButton type="submit" variant="contained" sx={{ fontSize: "1rem", textTransform: "none", width: "100%" }} loading={isLoading}>
                    Iniciar sesión
                  </LoadingButton>
                  <Stack spacing={{ xs: "2rem", sm: "4rem" }} direction="row">
                    <Typography color="primary" component={Link} to="/register" fontSize=".9rem">Registrarme</Typography>
                    <Typography color="primary" component={Link} to="/passwordRecovery/request" fontSize=".9rem">Olvidé mi contraseña</Typography>
                  </Stack>
                  <TermsAndConditionsLabel />
                </Stack>
              </Box>
            </Grid>
          </Paper>
        </form>
      ) : (
        <form onSubmit={handleCodeSubmit(verify)} style={{ height: "100%", width: "100%" }} noValidate>
          <Paper style={{ height: "100%", display: "flex", alignItems: "center", backgroundPosition: "top", backgroundRepeat: "no-repeat", backgroundImage: "url(https://res.cloudinary.com/dw86qy8xu/image/upload/v1694744779/loginSplash_syhrc1.jpg)", backgroundSize: "cover" }}>
            <Grid item xs={12} sm={8} md={6} lg={4} marginX="auto" boxSizing="border-box" p="1rem">
              <Box p="1rem" boxSizing="border-box" borderRadius="16px" boxShadow="0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px rgba(0, 0, 0, 0.15)" bgcolor="#FAFAFA">
                <Stack direction="column" alignItems="center" p="1rem" spacing="2rem">
                  <Logo />
                  <Typography align="center" fontSize=".9rem">
                    Ingresá el código de verificación que te enviamos a {email}.
                    <br />Para mantener tu seguridad, te lo vamos a pedir <strong>una vez por dispositivo.</strong>
                  </Typography>
                  <Stack width="100%" spacing="1rem" pt="1rem">
                    <TextField control={codeControl} name="code" type="text" label="Código de verificación" variant="outlined" style={{ width: "100%" }} required inputMode="numeric" autoComplete="one-time-code" />
                  </Stack>
                  <LoadingButton type="submit" variant="contained" sx={{ fontSize: "1rem", textTransform: "none", width: "100%" }} loading={isLoading}>
                    Verificar código
                  </LoadingButton>
                  <Typography color="primary" onClick={() => backToCredentials(true)} fontSize=".9rem" sx={{ cursor: "pointer" }}>
                    Volver a iniciar sesión
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Paper>
        </form>
      )}
      <ErrorAlert
        show={failed}
        onClose={onAlertClose}
        message={message}
      />
      <Alert
        severity="info"
        show={!!notice}
        onClose={() => setNotice(null)}
        message={notice}
      />
      <Alert
        severity="warning"
        alwaysVisible
        keepOpen
        show={passwordResetRequired}
        message={passwordResetRequiredMessage}
      />
    </Stack>
  );
};

export default Login;